<template>
  <main-large-dialog
    :isValid="isValid"
    :is-visible.sync="computedIsVisible"
    :loading="isLoading"
    :title="$i18n.t('dialog.addDataSource')"
    min-body-height="55vh"
    dialog-width="70%"
    header-shadow
    disagree-text="dialog.disagree"
    no-paddings-x
    agree-text="dialog.agreeAdd"
    @agree="addDataSourceHandler"
  >
    <template #body>
      <v-col cols="12">
        <v-row no-gutters class="border-bottom pb-2">
          <v-col cols="5" class="px-5">
            <v-form v-model="isValid" ref="form">
              <v-row no-gutters align="center">
                <v-col cols="5" class="text-left">
                  <span> {{ $i18n.t('dialog.name') }}: </span>
                </v-col>
                <v-spacer />
                <v-col cols="7" class="dict-value">
                  <data-input
                    class="pl-4"
                    v-model="payload.datasource_verbose_name"
                    :data-type="dataTypeText"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters align="center">
                <v-col cols="5" class="text-left">
                  <span> {{ $i18n.t('dialog.geometryType') }}: </span>
                </v-col>
                <v-spacer />
                <v-col cols="7" class="dict-value">
                  <data-input
                    class="pl-4"
                    v-model="payload.geometry_type"
                    :data-type="dataTypeSelect"
                    :items="geomTypes"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-col>
        </v-row>
        <v-row no-gutters class="border-bottom">
          <v-col class="py-9 pl-5 manage-text text-left">
            {{ $i18n.t('dialog.manageNewDataSourceAttributes') }}
          </v-col>
        </v-row>
        <v-row no-gutters justify="center" align="center" class="py-3">
          <v-col class="column-title" v-for="columnTitle of columnsTitles" :key="columnTitle.name">
            <span>
              {{ $i18n.t(columnTitle.translatePath) }}
            </span>
            <v-tooltip bottom color="font" v-if="columnTitle.info">
              <template #activator="{ on }">
                <v-icon v-on="on" small style="padding-bottom:3px" color="#0091EA">
                  mdi-information
                </v-icon>
              </template>
              <span>
                {{ $i18n.t(`${columnTitle.translatePath}Info`) }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <span v-for="(attribute, idx) of payload.attributes" :key="idx">
          <v-row no-gutters align="center" class="py-2 border-top justify-space-between" style="height: 64px">
            <v-col class="column-element-text">
              {{ attribute.verbose_name }}
            </v-col>
            <v-col class="column-element-text">
              {{ attribute.name }}
            </v-col>
            <v-col class="column-element-text">
              {{
                attribute.data_type
                  ? dataTypes.find(dT => dT.name === attribute.data_type).verbose_name
                  : attribute.data_type
              }}
            </v-col>
            <v-col class="column-element-text">
              {{ attribute.dict_id ? dictionaries[attribute.dict_id].name : '' }}
            </v-col>
            <v-col class="column-element-text">
              {{
                attribute.measurement_unit_id
                  ? flatMeasurementUnits.find(mU => mU.id === attribute.measurement_unit_id).name
                  : attribute.measurement_unit_id
              }}
            </v-col>
            <v-col>
              <span>
                <v-icon color="error" @click="deleteAttribute(attribute.name)">
                  mdi-close
                </v-icon>
              </span>
            </v-col>
          </v-row>
        </span>
        <v-form v-model="isNewAttributeValid" ref="newAttributeForm">
          <v-row no-gutters justify="center" align="center" class="py-1 border-top">
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newAttribute.verbose_name"
                :data-type="dataTypeText"
                :rules="[v => !!v || $i18n.t('rules.required')]"
              />
            </v-col>
            <v-col>
              <data-input
                class="px-5 py-0 ma-0"
                v-model="newAttribute.name"
                :data-type="dataTypeText"
                :rules="[v => !!v || /^[a-zA-Z]+[\w\d]*$/.test(v || '') || $i18n.t('rules.incorrectValue')]"
              />
            </v-col>
            <v-col>
              <data-input
                :menu-props="{ offsetY: true, minWidth: '200px' }"
                v-model="newAttribute.data_type"
                :items="excludedDataTypes"
                item-text="verbose_name"
                item-value="name"
                @change="newAttribute.dict_id = undefined"
                class="px-5"
                :data-type="dataTypeSelect"
                :rules="[v => !!v || $i18n.t('rules.required')]"
              />
            </v-col>
            <v-col>
              <v-tooltip bottom color="font" :disabled="!!newAttribute.data_type">
                <template #activator="{ on }">
                  <span v-on="on">
                    <data-input
                      :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                      v-model="newAttribute.dict_id"
                      :items="Object.values(dictionaries).filter(d => d.data_type === newAttribute.data_type)"
                      item-value="id"
                      item-text="name"
                      clearable
                      :disabled="!newAttribute.data_type"
                      class="px-5"
                      :data-type="dataTypeSelect"
                    >
                    </data-input>
                  </span>
                </template>
                {{ $i18n.t('dialog.body.dataTypeDict') }}
              </v-tooltip>
            </v-col>
            <v-col>
              <data-input
                :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                :items="formattedMeasurementUnits"
                item-text="text"
                item-value="id"
                v-model="newAttribute.measurement_unit_id"
                clearable
                class="px-5"
                :data-type="dataTypeSelect"
              />
            </v-col>
            <v-col>
              <v-icon
                :style="`cursor: ${isNewAttributeValid ? 'pointer' : 'default'}`"
                :color="isNewAttributeValid ? 'success' : 'grey'"
                @click="pushAttribute"
              >
                mdi-check
              </v-icon>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
export default {
  name: 'NewDataSourceDialog',
  components: {
    DataInput,
    MainLargeDialog,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dataTypes: get('layers/dataTypes'),
    dictionaries: get('admin/dictionaries'),
    excludedDataTypes() {
      return this.dataTypes.filter(d => d.name !== 'list' && d.name !== 'geometric');
    },
    formattedMeasurementUnits() {
      let formattedMeasurementUnits = [];
      for (let measurementUnitGroup of Object.keys(this.measurementUnits)) {
        formattedMeasurementUnits.push({ header: measurementUnitGroup });
        formattedMeasurementUnits = [
          ...formattedMeasurementUnits,
          ...this.measurementUnits[measurementUnitGroup].map(mU => {
            return { id: mU.id, text: `${mU.name} [${mU.symbol}]` };
          }),
        ];
        formattedMeasurementUnits.push({ divider: true });
      }
      return formattedMeasurementUnits;
    },
    geomTypes() {
      return [
        {
          value: 'point',
          text: this.$i18n.t('dialog.style.point'),
        },
        {
          value: 'linestring',
          text: this.$i18n.t('dialog.style.line'),
        },
        {
          value: 'polygon',
          text: this.$i18n.t('dialog.style.polygon'),
        },
      ];
    },
    measurementUnits: get('layers/measurementUnits'),
    flatMeasurementUnits() {
      let flatMeasurementUnits = [];
      for (const mU of Object.values(this.measurementUnits)) {
        flatMeasurementUnits = flatMeasurementUnits.concat(mU);
      }
      return flatMeasurementUnits;
    },
  },
  data: () => ({
    columnsTitles: [
      {
        name: 'verbose_name',
        translatePath: 'dialog.attributeName',
      },
      {
        name: 'name',
        translatePath: 'dialog.systemName',
        info: true,
      },
      {
        name: 'dataType',
        translatePath: 'dialog.dataType',
        info: true,
      },
      {
        name: 'dict',
        translatePath: 'dialog.dictionary',
        info: true,
      },
      {
        name: 'units',
        translatePath: 'dialog.units',
        info: true,
      },
      {
        name: 'delete',
        translatePath: 'dialog.deleteAttributes',
      },
    ],
    dataTypeText: {
      name: 'text',
    },
    dataTypeSelect: {
      name: 'select',
    },
    isLoaded: false,
    isNewAttributeValid: false,
    isLoading: false,
    isValid: false,
    newAttribute: {},
    payload: {},
  }),
  methods: {
    addDataSource: call('layers/addDataSource'),
    getDataTypes: call('layers/getDataTypes'),
    getDicts: call('admin/getDictionaries'),
    getMeasurementUnits: call('layers/getMeasurementUnits'),
    async addDataSourceHandler() {
      try {
        this.isLoading = true;
        const payload = JSON.parse(JSON.stringify(this.payload));
        payload.srid = this.$_config.defaultEpsg.split(':')[1];
        if (payload.attributes && payload.attributes.length > 0) {
          payload.attributes = payload.attributes.map(attr => {
            return { ...attr, data_type: { name: attr.data_type } };
          });
        }
        await this.addDataSource(payload);
        this.isLoading = false;
        this.computedIsVisible = false;
      } catch {
        this.isLoading = false;
        this.computedIsVisible = false;
      }
    },
    deleteAttribute(name) {
      const currentAttributes = JSON.parse(JSON.stringify(this.payload.attributes));
      const currentFilteredAttributes = currentAttributes.filter(attr => attr.name !== name);
      this.$set(this.payload, 'attributes', currentFilteredAttributes);
      this.$forceUpdate();
    },
    pushAttribute() {
      if (!this.isNewAttributeValid) {
        return;
      }
      if (!this.payload.attributes) {
        this.payload.attributes = [];
      }
      this.payload.attributes.push(this.newAttribute);
      this.newAttribute = {};
      this.$refs?.newAttributeForm?.resetValidation();
    },
    async init() {
      try {
        await Promise.all([this.getDicts(), this.getDataTypes(), this.getMeasurementUnits()]);
        this.isLoaded = true;
      } catch {
        this.isLoaded = true;
      }
    },
  },
  watch: {
    computedIsVisible(nV) {
      if (!nV) {
        this.payload = {};
        this.$refs?.newAttributeForm?.reset();
        this.$refs?.form?.reset();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
.column-title {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}
.column-element-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;

  overflow: hidden;
}
.manage-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  color: #202124;
}
.border-bottom {
  @include border(bottom, 1px);
}
.border-top {
  @include border(top, 1px);
}
</style>
